import React, { useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";

// ISO2 codes - must match seeded rows in Ref.Z_02_06_Country_Master (same
// list SEC_RepresentativeController uses, needed for the Dogma address lookup).
const VALID_COUNTRIES = ["GB", "FR", "CH", "DE"];

export default function SupplierView({
  companyName,
  data,
  loading,
  creating,
  concepts,
  conceptsReady,
  classificationsByRelationship,
  form,
  onPrimaryFieldChange,
  onCompanyFieldChange,
  registryStatus,
  registryResults,
  registryError,
  selectedMatchId,
  confirmedCompany,
  onCheckRegistration,
  onSelectMatch,
  onConfirmSelection,
  onRejectRegistryMatches,
  onDiscardConfirmedMatch,
  onAdd,
  onRevoke,
  onReactivate,
  onExpandClassification,
  onToggleClassification,
}) {
  const [expandedRelationshipId, setExpandedRelationshipId] = useState(null);

  const isValid = form.name && form.regNumber && form.country && form.addressLine1 && form.postcode;

  const toggleClassificationRow = (relationshipId) => {
    const next = expandedRelationshipId === relationshipId ? null : relationshipId;
    setExpandedRelationshipId(next);
    if (next) onExpandClassification(next);
  };

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      <div className="p-3 border-bottom bg-light">
        <h6 className="fw-bold extra-small text-uppercase text-primary mb-0">
          <i className="bi bi-truck me-1"></i> Suppliers — {companyName || "Selected Entity"}
        </h6>
        <span className="extra-small text-muted">
          Suppliers registered against this company. Classify each one against this company's connected ESG taxonomy.
        </span>
      </div>

      <div className="p-3 border-bottom bg-white">
          <div className="row g-2">
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={form.country}
                disabled={registryStatus === "confirmed"}
                onChange={(e) => onPrimaryFieldChange("country", e.target.value)}
              >
                <option value="">Country *</option>
                {VALID_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-5">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Supplier Name *"
                value={form.name}
                disabled={registryStatus === "confirmed"}
                onChange={(e) => onCompanyFieldChange("name", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Reg Number *"
                value={form.regNumber}
                disabled={registryStatus === "confirmed"}
                onChange={(e) => onCompanyFieldChange("regNumber", e.target.value)}
              />
            </div>
          </div>

          {/* Same registry-first flow as Register Company: check the official
              registry (Companies House / DINUM / Zefix) before falling back
              to manual entry below. A confirmed match populates the address
              directly and locks the search inputs above until discarded. */}
          <div className="mt-2">
            {registryStatus !== "confirmed" && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary fw-bold extra-small px-3"
                disabled={registryStatus === "checking"}
                onClick={onCheckRegistration}
              >
                {registryStatus === "checking" ? "CHECKING..." : "CHECK REGISTRATION"}
              </button>
            )}

            {registryError && (
              <div className="alert alert-warning py-1 px-2 small mt-2 mb-0">{registryError}</div>
            )}

            {registryStatus === "confirmed" && confirmedCompany && (
              <div className="alert alert-success py-2 px-3 small mt-2 mb-0">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-bold mb-1">
                      <i className="bi bi-check-circle-fill me-1"></i>
                      {confirmedCompany.companyName}
                    </div>
                    <address className="mb-0 extra-small lh-lg">
                      {confirmedCompany.addressLine1}<br />
                      {confirmedCompany.addressLine2 && <>{confirmedCompany.addressLine2}<br /></>}
                      {confirmedCompany.town && <>{confirmedCompany.town}<br /></>}
                      {confirmedCompany.county && <>{confirmedCompany.county}<br /></>}
                      {confirmedCompany.postcode}<br />
                      {form.country}
                    </address>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary extra-small px-2"
                    onClick={onDiscardConfirmedMatch}
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}

            {registryStatus === "rejected" && (
              <div className="alert alert-secondary py-1 px-2 small mt-2 mb-0">
                No registry match confirmed - enter the address manually below.
              </div>
            )}

            {registryStatus === "results" && (
              <div className="mt-2">
                <p className="extra-small text-muted fw-bold text-uppercase mb-2">
                  {registryResults.length === 1 ? "Is this your supplier?" : "Select the supplier"}
                </p>
                <div className="table-responsive">
                  <table className="table table-sm table-striped table-bordered align-middle mb-2">
                    <thead>
                      <tr>
                        <th className="extra-small text-muted" style={{ width: "2.5rem" }}></th>
                        <th className="extra-small text-muted text-uppercase">Company Name</th>
                        <th className="extra-small text-muted text-uppercase">ID</th>
                        <th className="extra-small text-muted text-uppercase">Address</th>
                        <th className="extra-small text-muted text-uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registryResults.map((match) => (
                        <tr
                          key={match.companyId}
                          onClick={() => onSelectMatch(match.companyId)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={String(selectedMatchId) === String(match.companyId)}
                              onChange={() => onSelectMatch(match.companyId)}
                            />
                          </td>
                          <td className="small fw-bold">{match.companyName}</td>
                          <td className="small">{match.companyId}</td>
                          <td className="small">{match.addressSnippet || "—"}</td>
                          <td className="small">{match.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-dark fw-bold extra-small px-3"
                  disabled={!selectedMatchId}
                  onClick={onConfirmSelection}
                >
                  CONFIRM SELECTION
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted extra-small ms-2"
                  onClick={onRejectRegistryMatches}
                >
                  None of these — enter manually
                </button>
              </div>
            )}
          </div>

          {registryStatus !== "confirmed" && (
            <div className="row g-2 mt-1">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Address Line 1 *"
                  value={form.addressLine1}
                  onChange={(e) => onPrimaryFieldChange("addressLine1", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Address Line 2"
                  value={form.addressLine2}
                  onChange={(e) => onPrimaryFieldChange("addressLine2", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Town"
                  value={form.town}
                  onChange={(e) => onPrimaryFieldChange("town", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="County"
                  value={form.county}
                  onChange={(e) => onPrimaryFieldChange("county", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Postcode *"
                  value={form.postcode}
                  onChange={(e) => onPrimaryFieldChange("postcode", e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="mt-2">
            <button
              className="btn btn-sm btn-dark fw-bold px-4"
              onClick={onAdd}
              disabled={creating || !isValid}
            >
              {creating ? "SAVING..." : "ADD SUPPLIER"}
            </button>
          </div>
      </div>

      {conceptsReady && concepts.length === 0 && (
        <div className="px-3 py-2 bg-white border-bottom">
          <span className="extra-small text-muted italic">
            No ESG taxonomy connected yet — classify suppliers once one is connected on the Taxonomy tab.
          </span>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th className="extra-small text-muted py-3 ps-3">SUPPLIER</th>
              <th className="extra-small text-muted py-3">REG ID</th>
              <th className="extra-small text-muted py-3">STATUS</th>
              <th className="extra-small text-muted py-3 text-end pe-3">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="text-center p-4 text-muted extra-small italic">Loading suppliers...</td></tr>
            ) : data.length > 0 ? (
              data.map((row, i) => {
                const relationshipId = GoldenSourceHelper.get(row, "RelationshipId");
                const supplierEntityId = GoldenSourceHelper.get(row, "CounterpartyEntityId");
                const isActive = GoldenSourceHelper.get(row, "IsActive");
                const isExpanded = expandedRelationshipId === relationshipId;
                const assigned = classificationsByRelationship[relationshipId] || [];
                const assignedRowIds = new Set(assigned.map((c) => GoldenSourceHelper.get(c, "TaxonomyDetailRowId")));

                return (
                  <React.Fragment key={relationshipId || i}>
                    <tr className={`small ${!isActive ? "text-muted" : ""}`}>
                      <td className="ps-3 fw-bold">{GoldenSourceHelper.get(row, "EntityName")}</td>
                      <td className="text-muted extra-small">{GoldenSourceHelper.get(row, "TaxReference")}</td>
                      <td className="extra-small">
                        {isActive ? (
                          <span className="badge bg-light text-dark border extra-small text-uppercase">Active</span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary border extra-small text-uppercase">Removed</span>
                        )}
                      </td>
                      <td className="text-end pe-3">
                        {isActive ? (
                          <>
                            <button
                              className="btn btn-link btn-sm p-0 me-3 extra-small fw-bold text-decoration-none"
                              onClick={() => toggleClassificationRow(relationshipId)}
                              disabled={concepts.length === 0}
                            >
                              {isExpanded ? "CLOSE" : `CLASSIFY${assigned.length ? ` (${assigned.length})` : ""}`}
                            </button>
                            <button
                              className="btn btn-link btn-sm p-0 extra-small fw-bold text-decoration-none text-danger"
                              onClick={() => onRevoke(supplierEntityId)}
                            >
                              Remove
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-link btn-sm p-0 extra-small fw-bold text-decoration-none"
                            onClick={() => onReactivate(supplierEntityId)}
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="4" className="p-3 bg-light border-top-0">
                          <span className="extra-small fw-bold text-uppercase text-muted d-block mb-2">
                            What does this supplier provide? (this company's ESG taxonomy)
                          </span>
                          <div className="d-flex flex-column gap-1" style={{ maxHeight: "220px", overflowY: "auto" }}>
                            {concepts.map((concept) => {
                              const rowId = GoldenSourceHelper.get(concept, "RowId");
                              const isChecked = assignedRowIds.has(rowId);
                              return (
                                <label key={rowId} className="d-flex align-items-center gap-2 extra-small">
                                  <input
                                    type="checkbox"
                                    className="form-check-input m-0"
                                    checked={isChecked}
                                    onChange={() => onToggleClassification(relationshipId, rowId, isChecked)}
                                  />
                                  {GoldenSourceHelper.get(concept, "MeasureName") || GoldenSourceHelper.get(concept, "XbrlTag")}
                                </label>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr><td colSpan="4" className="text-center p-4 text-muted extra-small italic">No suppliers registered for this company yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
